import { useEffect, useRef, useState } from 'react';
import type { MatchResult, Lineup } from '@stanwars/sim';
import { buildPlayback, type Frame, type UnitKey } from './playback';
import { clearCanvas, drawRect, drawText, drawFrame } from './renderer';
import { loadSpriteSheet, type SpriteSheet } from './spriteLoader';
import { useViewportFit } from '../util/viewport';

const LOGICAL_W = 480;
const LOGICAL_H = 270;
const FPS = 30;

// Stage layout constants
const STAGE_EDGE_Y = 188;   // neon line separating floor from "sky"
const FLOOR_GRID_COLOR = '#1C1640';

// Staggered 2.5D formation: front units near center, back units far out
// Slot 0-1 = front row (closer to center, lower y), 2-4 = back row
const FORMATION: Record<'A' | 'B', ReadonlyArray<readonly [number, number]>> = {
  A: [
    [105, 170], // slot 0: front-center
    [70,  152], // slot 1: front-inner
    [52,  140], // slot 2: mid
    [16,  120], // slot 3: back-far
    [62,  108], // slot 4: back-high
  ],
  B: [
    [343, 170], // mirror of A: 480-105-32
    [378, 152], // 480-70-32
    [396, 140], // 480-52-32
    [432, 120], // 480-16-32
    [386, 108], // 480-62-32
  ],
};

const ROLE_COLORS: Record<string, string> = {
  rapper:   '#FF3AA6',
  vocalist: '#3AF0FF',
  dancer:   '#FFD23A',
  visual:   '#3AFF8E',
  maknae:   '#FF8E3A',
};

interface Props {
  result: MatchResult;
  lineupA: Lineup;
  lineupB: Lineup;
  onComplete?: () => void;
  startFrame?: number;
}

export function BattleScene({ result, lineupA, lineupB, onComplete, startFrame = 0 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [skipAvailable, setSkipAvailable] = useState(false);
  const [done, setDone] = useState(false);
  const fit = useViewportFit(960);
  const sheetsRef = useRef<Map<UnitKey, SpriteSheet>>(new Map());
  const backdropRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const allSlots: Array<{ key: UnitKey; memberId: string }> = [];
    for (let slot = 0; slot < 5; slot++) {
      allSlots.push({ key: `A${slot}` as UnitKey, memberId: lineupA.slots[slot]!.member.id });
      allSlots.push({ key: `B${slot}` as UnitKey, memberId: lineupB.slots[slot]!.member.id });
    }
    for (const { key, memberId } of allSlots) {
      loadSpriteSheet(`/stanwars/sprites/members/${memberId}/base.png`, 64, 64)
        .then(sheet => { sheetsRef.current.set(key, sheet); })
        .catch(() => {});
    }

    const groupCounts = new Map<string, number>();
    for (const s of lineupA.slots) groupCounts.set(s.member.groupId, (groupCounts.get(s.member.groupId) ?? 0) + 1);
    const dominantGroup = [...groupCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    if (dominantGroup) {
      const img = new Image();
      img.onload = () => { backdropRef.current = img; };
      img.src = `/stanwars/sprites/groups/${dominantGroup}.png`;
    }
  }, [lineupA, lineupB]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = LOGICAL_W;
    canvas.height = LOGICAL_H;

    const initialHpA = lineupA.slots.map(s => s.member.stats.hp);
    const initialHpB = lineupB.slots.map(s => s.member.stats.hp);
    const playback = buildPlayback(result.events, FPS, initialHpA, initialHpB);

    let frameIdx = Math.min(startFrame, playback.totalFrames - 1);
    let raf = 0;
    let last = performance.now();
    const skipTimer = window.setTimeout(() => setSkipAvailable(true), 5000);

    const tick = (now: number) => {
      const dt = now - last;
      if (dt >= 1000 / FPS) {
        last = now;
        renderFrame(ctx, playback.frames[frameIdx]!, lineupA, lineupB, playback.maxHpA, playback.maxHpB, sheetsRef.current, frameIdx, backdropRef.current);
        frameIdx += 1;
        if (frameIdx >= playback.totalFrames) {
          setDone(true);
          if (onComplete) onComplete();
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(skipTimer);
    };
  }, [result, lineupA, lineupB, onComplete]);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4">
      <canvas
        ref={canvasRef}
        className="pixel border-2 border-neon-pink"
        style={{ width: `${fit.cssWidth}px`, height: `${fit.cssHeight}px` }}
      />
      {skipAvailable && !done && (
        <button
          className="pixel-text uppercase tracking-widest px-4 py-2 bg-neon-cyan text-ink-900 border-2 border-ink-900"
          onClick={() => onComplete?.()}
        >
          Skip ▶
        </button>
      )}
      {done && (
        <div className="pixel-text text-neon-yellow text-lg">
          {result.winner === 'draw' ? 'DRAW' : `${result.winner} WINS`}
        </div>
      )}
    </div>
  );
}

// Sprite sheet row mapping: row 0=idle, 1=attack, 2=hurt, 3=ko
const STATE_ROW: Record<string, number> = { idle: 0, attack: 1, hurt: 2, ko: 3 };
const FRAMES_PER_ROW = 4;
const COLS_PER_ROW = 4;

function spriteFrameIndex(state: string, playbackFrame: number): number {
  const row = STATE_ROW[state] ?? 0;
  const col = state === 'ko' ? FRAMES_PER_ROW - 1 : Math.floor(playbackFrame / 8) % FRAMES_PER_ROW;
  return row * COLS_PER_ROW + col;
}

function drawBackground(ctx: CanvasRenderingContext2D, backdrop: HTMLImageElement | null): void {
  // Sky gradient
  const sky = ctx.createLinearGradient(0, 0, 0, STAGE_EDGE_Y);
  sky.addColorStop(0, '#08061A');
  sky.addColorStop(1, '#170F38');
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, LOGICAL_W, STAGE_EDGE_Y);

  // Backdrop group art (subtle, top half only)
  if (backdrop && backdrop.complete && backdrop.naturalWidth > 0) {
    ctx.save();
    ctx.globalAlpha = 0.14;
    ctx.drawImage(backdrop, 0, 0, LOGICAL_W, STAGE_EDGE_Y);
    ctx.restore();
  }

  // Stage floor
  const floor = ctx.createLinearGradient(0, STAGE_EDGE_Y, 0, LOGICAL_H);
  floor.addColorStop(0, '#1C1640');
  floor.addColorStop(1, '#0E0C1A');
  ctx.fillStyle = floor;
  ctx.fillRect(0, STAGE_EDGE_Y, LOGICAL_W, LOGICAL_H - STAGE_EDGE_Y);

  // Floor perspective lines (radiate from vanishing point)
  ctx.strokeStyle = FLOOR_GRID_COLOR;
  ctx.lineWidth = 1;
  const vp = { x: LOGICAL_W / 2, y: STAGE_EDGE_Y };
  const steps = 10;
  for (let i = 0; i <= steps; i++) {
    const bx = (i / steps) * LOGICAL_W;
    ctx.beginPath();
    ctx.moveTo(vp.x, vp.y);
    ctx.lineTo(bx, LOGICAL_H);
    ctx.stroke();
  }

  // Stage edge neon line
  ctx.strokeStyle = '#FF3AA6';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, STAGE_EDGE_Y);
  ctx.lineTo(LOGICAL_W, STAGE_EDGE_Y);
  ctx.stroke();

  // Center divider (faint)
  ctx.strokeStyle = '#2A1850';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(LOGICAL_W / 2, 60);
  ctx.lineTo(LOGICAL_W / 2, STAGE_EDGE_Y);
  ctx.stroke();

  // "VS" at center
  drawText(ctx, 'VS', LOGICAL_W / 2, 128, { color: '#3A2860', size: 16, align: 'center' });
}

function drawHpBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  hp: number,
  maxHp: number,
): void {
  const pct = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
  // Background track
  drawRect(ctx, x, y, w, 4, '#0A0820');
  // Fill color: green > yellow > red
  const fillColor = pct > 0.5 ? '#3AFF8E' : pct > 0.25 ? '#FFD23A' : '#FF4444';
  drawRect(ctx, x, y, Math.ceil(w * pct), 4, fillColor);
  // Outline
  ctx.strokeStyle = '#0A0820';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, 3);
}

function drawSlashVfx(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  ageFrames: number,
  side: 'A' | 'B',
): void {
  const alpha = Math.max(0, 1 - ageFrames / 24);
  const size = 8 + ageFrames * 1.5;
  const color = side === 'A' ? '#FFD23A' : '#FF3AA6';
  ctx.save();
  ctx.globalAlpha = alpha * 0.9;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  // X-shaped slash
  ctx.beginPath();
  ctx.moveTo(x - size, y - size); ctx.lineTo(x + size, y + size);
  ctx.moveTo(x + size, y - size); ctx.lineTo(x - size, y + size);
  ctx.stroke();
  // Horizontal accent line
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x - size * 1.2, y); ctx.lineTo(x + size * 1.2, y);
  ctx.stroke();
  ctx.restore();
}

function renderFrame(
  ctx: CanvasRenderingContext2D,
  frame: Frame,
  lineupA: Lineup,
  lineupB: Lineup,
  maxHpA: readonly number[],
  maxHpB: readonly number[],
  sheets: Map<UnitKey, SpriteSheet>,
  playbackFrame: number,
  backdrop: HTMLImageElement | null,
): void {
  clearCanvas(ctx, '#08061A');
  drawBackground(ctx, backdrop);

  const drawSide = (side: 'A' | 'B', lineup: Lineup) => {
    const positions = FORMATION[side];
    const maxHp = side === 'A' ? maxHpA : maxHpB;
    const frameHp = side === 'A' ? frame.hpA : frame.hpB;

    for (let slot = 0; slot < 5; slot++) {
      const [x, y] = positions[slot]!;
      const lineupSlot = lineup.slots[slot]!;
      const role = lineupSlot.member.role;
      const unit = `${side}${slot}` as UnitKey;
      const state = frame.spriteStates.get(unit) ?? 'idle';
      const sheet = sheets.get(unit);

      if (sheet) {
        const frameIdx = spriteFrameIndex(state, playbackFrame);
        drawFrame(ctx, sheet, frameIdx, x, y, side === 'B');
        if (state === 'ko') {
          ctx.save(); ctx.globalAlpha = 0.6;
          drawRect(ctx, x, y, 32, 32, '#000000');
          ctx.restore();
        }
        if (state === 'hurt') {
          ctx.save(); ctx.globalAlpha = 0.4;
          drawRect(ctx, x, y, 32, 32, '#E84B4B');
          ctx.restore();
        }
      } else {
        const color = state === 'ko' ? '#444' : (ROLE_COLORS[role] ?? '#FFFFFF');
        drawRect(ctx, x, y, 32, 32, color);
        if (state === 'attack') {
          // Simple flash outline
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 2;
          ctx.strokeRect(x - 1, y - 1, 34, 34);
        }
        if (state === 'hurt') {
          ctx.save(); ctx.globalAlpha = 0.5;
          drawRect(ctx, x, y, 32, 32, '#E84B4B');
          ctx.restore();
        }
      }

      // HP bar below the unit sprite
      const hp = frameHp[slot] ?? 0;
      const mhp = maxHp[slot] ?? 100;
      drawHpBar(ctx, x, y + 34, 32, hp, mhp);
    }
  };

  drawSide('A', lineupA);
  drawSide('B', lineupB);

  // Damage numbers — positioned near actual unit
  for (const dn of frame.damageNumbers) {
    const slot = parseInt(dn.unit.slice(1), 10);
    const side = dn.unit[0] as 'A' | 'B';
    const [ux, uy] = FORMATION[side][slot]!;
    const x = ux + 16; // center of unit
    const y = uy - dn.ageFrames * 0.8 - 4;
    const alpha = Math.max(0, 1 - dn.ageFrames / 30);
    ctx.save();
    ctx.globalAlpha = alpha;
    drawText(ctx, String(dn.amount), x, y, {
      color: dn.isCrit ? '#FFD23A' : '#FFFFFF',
      size: dn.isCrit ? 10 : 7,
      align: 'center',
    });
    ctx.restore();
  }

  // VFX: X-slash at attacker position
  for (const vfx of frame.vfxSpawns) {
    const slot = parseInt(vfx.unit.slice(1), 10);
    const side = vfx.unit[0] as 'A' | 'B';
    const [ux, uy] = FORMATION[side][slot]!;
    drawSlashVfx(ctx, ux + 16, uy + 16, vfx.ageFrames, side);
  }

  // Title
  drawText(ctx, 'STAN WARS', 6, 6, { color: '#FF3AA6', size: 7 });
  // Side labels
  drawText(ctx, 'YOU', 6, 16, { color: '#3AF0FF', size: 5 });
  drawText(ctx, 'ENEMY', LOGICAL_W - 6, 16, { color: '#FF8E3A', size: 5, align: 'right' });
}
